import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { useAuth } from "../../provider/AuthProvider";
import { useLoginModal } from "../../context/LoginModalContext";
import { VELLUM as T, SANS, display, mono, body, btn } from "../shared/folio/kit";
import { FP, SheetTitle, FieldBlock, FieldRow, CheckRow, ErrorNote, fieldInput } from "../shared/folio/form";
import { FolioSelect } from "../shared/folio/Select";
import { DIALOG_TITLE_ID } from "./LoginModalContent";

/* Create an account, drawn as a sheet.

   The fields are a title block, filled in from the top. Which party is
   registering is settled first, in its own block, because it changes what
   the account is for - not buried as a preference among the fields. */

const TITLE_OPTIONS = ["Dr.", "Prof.", "Mr.", "Mrs.", "Ms.", "Mx."];

const header: React.CSSProperties = {
  padding: "34px 32px 22px",
  borderBottom: `1.5px solid ${T.ink}`,
};

const consentLink: React.CSSProperties = {
  color: T.accent,
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

export const RegisterModalContent: React.FC = () => {
  const [role, setRole] = useState<"employee" | "employer">("employee");
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
  });
  const [consents, setConsents] = useState({
    termsConsent: false,
    dataProcessingConsent: false,
    marketingConsent: false,
  });
  const [consentError, setConsentError] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { register, loading } = useAuth();
  const { closeRegisterModal, switchToLogin } = useLoginModal();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!consents.termsConsent || !consents.dataProcessingConsent) {
      setConsentError(true);
      return;
    }
    const fullFirstName = formData.title ? `${formData.title} ${formData.firstName}` : formData.firstName;
    const payload = {
      firstName: fullFirstName,
      lastName: formData.lastName || undefined,
      phone: formData.phone,
      email: formData.email,
      password: formData.password,
      termsConsent: consents.termsConsent,
      dataProcessingConsent: consents.dataProcessingConsent,
      marketingConsent: consents.marketingConsent,
    };
    try {
      await register(payload, role);
      closeRegisterModal();
      if (formData.email) {
        navigate(ROUTE_NAMES.PAGES.VERIFY_EMAIL, { state: { email: formData.email, role } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed. Please try again.");
    }
  };

  return (
    <div style={{ fontFamily: SANS, color: T.ink }}>
      <div style={header}>
        <SheetTitle
          id={DIALOG_TITLE_ID}
          code="§ register"
          title="Create an account."
          standfirst="Join the academic employment register - for scholars and institutions both."
        />
      </div>

      <div style={{ padding: "26px 32px 32px", display: "grid", gap: 20 }}>
        {error && <ErrorNote>{error}</ErrorNote>}

        <div
          role="group"
          aria-label="Account type"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: `1.5px solid ${T.ink}`, background: T.ground }}
        >
          {(
            [
              { id: "employee", title: "Scholar", sub: "Find a position", code: "A · For scholars" },
              { id: "employer", title: "Institution", sub: "Post a position", code: "B · For institutions" },
            ] as const
          ).map((r, i) => {
            const on = role === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setRole(r.id)}
                aria-pressed={on}
                className={`${FP}-role`}
                style={{
                  position: "relative",
                  background: on ? T.ink : "transparent",
                  color: on ? T.ground : T.ink,
                  border: "none",
                  borderLeft: i === 1 ? `1px solid ${on ? T.ink : T.rule}` : "none",
                  borderRadius: 0,
                  padding: "15px 16px",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: SANS,
                }}
              >
                {/* A registration mark, not a coloured bar across the edge. */}
                {on && (
                  <span
                    aria-hidden
                    style={{ position: "absolute", top: 9, right: 9, width: 7, height: 7, background: T.accent }}
                  />
                )}
                <span style={{ ...mono(T, on ? T.rule : T.mute), display: "block" }}>{r.code}</span>
                <span style={{ ...display(T, "18px", on ? T.ground : T.ink), display: "block", marginTop: 6 }}>
                  {r.title}
                </span>
                <span style={{ ...body(T, on ? T.rule : T.mute), fontSize: 12.5, display: "block", marginTop: 3 }}>
                  {r.sub}
                </span>
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 20 }}>
          <FieldBlock>
            <FieldRow label="Title" htmlFor="reg-title" first>
              <FolioSelect
                bare
                id="reg-title"
                name="title"
                placeholder="—"
                value={formData.title}
                onChange={(v) => setFormData((prev) => ({ ...prev, title: v }))}
                options={TITLE_OPTIONS}
              />
            </FieldRow>

            <FieldRow label="First name" htmlFor="reg-first" required>
              <input
                id="reg-first"
                name="firstName"
                required
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Eleanor"
                style={fieldInput}
              />
            </FieldRow>

            <FieldRow label="Last name" htmlFor="reg-last">
              <input
                id="reg-last"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Marsh"
                style={fieldInput}
              />
            </FieldRow>

            <FieldRow label="Email" htmlFor="reg-email" required>
              <input
                id="reg-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.marsh@university.ac.uk"
                style={fieldInput}
              />
            </FieldRow>

            <FieldRow label="Phone" htmlFor="reg-phone" required>
              <input
                id="reg-phone"
                type="tel"
                name="phone"
                required
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+44 …"
                style={fieldInput}
              />
            </FieldRow>

            <FieldRow label="Password" htmlFor="reg-password" required>
              <input
                id="reg-password"
                type="password"
                name="password"
                required
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                placeholder="•••••••••"
                style={fieldInput}
              />
            </FieldRow>
          </FieldBlock>

          <div style={{ display: "grid", gap: 11 }}>
            {consentError && <ErrorNote>You must accept to continue</ErrorNote>}

            <CheckRow
              checked={consents.termsConsent}
              onChange={(c) => {
                setConsents({ ...consents, termsConsent: c });
                if (c && consents.dataProcessingConsent) setConsentError(false);
              }}
              required
              error={consentError && !consents.termsConsent}
            >
              I agree to the{" "}
              <a href={ROUTE_NAMES.PAGES.TERMS} target="_blank" rel="noopener noreferrer" className={`${FP}-link`} style={consentLink}>
                Terms of Service
              </a>{" "}
              and{" "}
              <a href={ROUTE_NAMES.PAGES.PRIVACY} target="_blank" rel="noopener noreferrer" className={`${FP}-link`} style={consentLink}>
                Privacy Policy
              </a>
            </CheckRow>

            <CheckRow
              checked={consents.dataProcessingConsent}
              onChange={(c) => {
                setConsents({ ...consents, dataProcessingConsent: c });
                if (c && consents.termsConsent) setConsentError(false);
              }}
              required
              error={consentError && !consents.dataProcessingConsent}
            >
              I consent to the processing of my personal data as described in the{" "}
              <a href={ROUTE_NAMES.PAGES.PRIVACY} target="_blank" rel="noopener noreferrer" className={`${FP}-link`} style={consentLink}>
                Privacy Policy
              </a>
            </CheckRow>

            <CheckRow
              checked={consents.marketingConsent}
              onChange={(c) => setConsents({ ...consents, marketingConsent: c })}
            >
              I'd like to receive marketing emails about new features and opportunities (optional)
            </CheckRow>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`${FP}-btn`}
            style={{ ...btn(T, "accent"), width: "100%", justifyContent: "center" }}
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <div
          style={{
            borderTop: `1px solid ${T.rule}`,
            paddingTop: 18,
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
            fontFamily: SANS,
            fontSize: 13.5,
            color: T.mute,
          }}
        >
          Already have an account?
          <button
            type="button"
            onClick={switchToLogin}
            className={`${FP}-quiet`}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: `1px solid ${T.ink}`,
              padding: "0 0 2px",
              cursor: "pointer",
              fontFamily: SANS,
              fontSize: 13.5,
              fontWeight: 600,
              color: T.ink,
            }}
          >
            Sign in →
          </button>
        </div>
      </div>
    </div>
  );
};
